/**
 * MaternityDetail.jsx
 * 
 * Detailed view of a single maternity record with tabs.
 * Styled with MUI components matching existing patterns.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../axiosConfig';
import MaternityTimeline from './MaternityTimeline';
import KITDayForm from './KITDayForm';
import MaternityLetterGenerator from './MaternityLetterGenerator';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Alert,
    Grid,
    Chip,
    Tabs,
    Tab,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    LinearProgress
} from '@mui/material';
import {
    ChildCare as MaternityIcon,
    ArrowBack as BackIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    Description as LetterIcon,
    Event as EventIcon,
    Schedule as ScheduleIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon,
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    WorkOff as KITIcon,
    Chat as CommsIcon,
    Timeline as TimelineIcon,
    Add as AddIcon,
    Close as CloseIcon
} from '@mui/icons-material';

const MaternityDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [record, setRecord] = useState(null);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    
    // Modals
    const [kitModalOpen, setKitModalOpen] = useState(false);
    const [letterModalOpen, setLetterModalOpen] = useState(false);
    const [commsModalOpen, setCommsModalOpen] = useState(false);
    const [commsForm, setCommsForm] = useState({
        type: 'general',
        method: 'email',
        direction: 'outbound',
        summary: ''
    });

    useEffect(() => {
        fetchRecord();
    }, [id]);

    const fetchRecord = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/maternity/${id}`);
            setRecord(res.data);
        } catch (err) {
            console.error('Failed to fetch maternity record', err);
            setError('Failed to load record');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            await api.put(`/maternity/${id}`, { status: newStatus });
            fetchRecord();
        } catch (err) {
            console.error('Failed to update status', err);
        }
    };

    const handleDismissReminder = async (reminderId) => {
        try {
            await api.post(`/maternity/reminders/${reminderId}/dismiss`);
            fetchRecord();
        } catch (err) {
            console.error('Failed to dismiss reminder', err);
        }
    };

    const handleLogCommunication = async () => {
        try {
            await api.post(`/maternity/${id}/communication`, commsForm);
            setCommsModalOpen(false);
            setCommsForm({ type: 'general', method: 'email', direction: 'outbound', summary: '' });
            fetchRecord();
        } catch (err) {
            console.error('Failed to log communication', err);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const getStatusChip = (status) => {
        const statusConfig = {
            notified: { label: 'Notified', color: 'info' },
            on_leave: { label: 'On Leave', color: 'success' },
            returned: { label: 'Returned', color: 'default' },
            resigned: { label: 'Resigned', color: 'error' },
            extended: { label: 'Extended', color: 'warning' },
            cancelled: { label: 'Cancelled', color: 'default' }
        };
        const config = statusConfig[status] || { label: status, color: 'default' };
        return <Chip label={config.label} color={config.color} />;
    };

    const calculateProgress = () => {
        if (!record?.intended_start_date || !record?.expected_return_date) return 0;
        const start = new Date(record.intended_start_date);
        const end = new Date(record.expected_return_date);
        const now = new Date();
        
        if (now < start) return 0;
        if (now > end) return 100;
        
        const total = end - start;
        const elapsed = now - start;
        return Math.round((elapsed / total) * 100);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !record) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="error">{error || 'Record not found'}</Alert>
                <Button startIcon={<BackIcon />} onClick={() => navigate('/team/maternity')} sx={{ mt: 2 }}>
                    Back to Tracker
                </Button>
            </Container>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                    color: 'white',
                    p: 4,
                    mb: 4,
                    borderRadius: 0
                }}
            >
                <Container maxWidth="lg">
                    <Button
                        startIcon={<BackIcon />}
                        onClick={() => navigate('/team/maternity')}
                        sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                    >
                        Back to Tracker
                    </Button>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ 
                                width: 64, 
                                height: 64, 
                                borderRadius: 2, 
                                bgcolor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 24,
                                fontWeight: 600
                            }}>
                                {record.employee_name?.charAt(0) || 'E'}
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {record.employee_name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {getStatusChip(record.status)}
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Due: {formatDate(record.ewc)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => navigate(`/team/maternity/${id}/edit`)}
                                sx={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white', '&:hover': { borderColor: 'white' } }}
                            >
                                Edit
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<LetterIcon />}
                                onClick={() => setLetterModalOpen(true)}
                                sx={{ bgcolor: 'white', color: '#7c3aed', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
                            >
                                Generate Letter
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Paper>

            <Container maxWidth="lg">
                {/* Progress Bar */}
                {record.status === 'on_leave' && (
                    <Card elevation={0} sx={{ border: '1px solid #e5e7eb', mb: 4 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">Leave Progress</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{calculateProgress()}% complete</Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={calculateProgress()} 
                                sx={{ 
                                    height: 8, 
                                    borderRadius: 4,
                                    bgcolor: '#f5f3ff',
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: '#8b5cf6'
                                    }
                                }} 
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Started: {formatDate(record.intended_start_date)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Return: {formatDate(record.expected_return_date)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Pending Reminders Alert */}
                {record.pending_reminders?.length > 0 && (
                    <Alert 
                        severity="warning" 
                        sx={{ mb: 4 }}
                        action={
                            <Button 
                                color="inherit" 
                                size="small"
                                onClick={() => handleDismissReminder(record.pending_reminders[0].id)}
                            >
                                Dismiss
                            </Button>
                        }
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {record.pending_reminders[0].reminder_text}
                        </Typography>
                        <Typography variant="caption">
                            Due: {formatDate(record.pending_reminders[0].reminder_date)}
                        </Typography>
                    </Alert>
                )}

                {/* Tabs */}
                <Card elevation={0} sx={{ border: '1px solid #e5e7eb' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ borderBottom: '1px solid #e5e7eb', px: 2 }}
                    >
                        <Tab icon={<PersonIcon />} iconPosition="start" label="Overview" />
                        <Tab icon={<TimelineIcon />} iconPosition="start" label="Timeline" />
                        <Tab icon={<KITIcon />} iconPosition="start" label={`KIT Days (${record.kit_days?.length || 0}/10)`} />
                        <Tab icon={<CommsIcon />} iconPosition="start" label="Communications" />
                    </Tabs>

                    <CardContent sx={{ p: 4 }}>
                        {/* Overview Tab */}
                        {activeTab === 0 && (
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Key Dates</Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon><CalendarIcon sx={{ color: '#8b5cf6' }} /></ListItemIcon>
                                            <ListItemText 
                                                primary="Expected Week of Childbirth" 
                                                secondary={formatDate(record.ewc)} 
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon><EventIcon sx={{ color: '#8b5cf6' }} /></ListItemIcon>
                                            <ListItemText 
                                                primary="Notification Date" 
                                                secondary={formatDate(record.notification_date)} 
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon><ScheduleIcon sx={{ color: '#22c55e' }} /></ListItemIcon>
                                            <ListItemText 
                                                primary="Leave Start Date" 
                                                secondary={formatDate(record.intended_start_date)} 
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon><CheckIcon sx={{ color: '#3b82f6' }} /></ListItemIcon>
                                            <ListItemText 
                                                primary="OML Ends (26 weeks)" 
                                                secondary={formatDate(record.oml_end_date)} 
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon><CheckIcon sx={{ color: '#3b82f6' }} /></ListItemIcon>
                                            <ListItemText 
                                                primary="AML Ends (52 weeks)" 
                                                secondary={formatDate(record.aml_end_date)} 
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon><WarningIcon sx={{ color: '#f59e0b' }} /></ListItemIcon>
                                            <ListItemText 
                                                primary="Extension Deadline" 
                                                secondary={formatDate(record.extension_deadline)} 
                                            />
                                        </ListItem>
                                    </List>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Status & Actions</Typography>
                                    
                                    <Card variant="outlined" sx={{ mb: 3, bgcolor: '#f5f3ff' }}>
                                        <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                                Current Status
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {['notified', 'on_leave', 'returned', 'extended', 'resigned'].map((status) => (
                                                    <Chip
                                                        key={status}
                                                        label={status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        onClick={() => handleStatusChange(status)}
                                                        color={record.status === status ? 'primary' : 'default'}
                                                        variant={record.status === status ? 'filled' : 'outlined'}
                                                        sx={{ 
                                                            textTransform: 'capitalize',
                                                            ...(record.status === status && { bgcolor: '#8b5cf6' })
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </CardContent>
                                    </Card>

                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Quick Actions</Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<LetterIcon />}
                                            onClick={() => setLetterModalOpen(true)}
                                            fullWidth
                                            sx={{ justifyContent: 'flex-start', borderColor: '#e5e7eb' }}
                                        >
                                            Generate Letter
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<KITIcon />}
                                            onClick={() => setKitModalOpen(true)}
                                            fullWidth
                                            sx={{ justifyContent: 'flex-start', borderColor: '#e5e7eb' }}
                                        >
                                            Record KIT Day
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<CommsIcon />}
                                            onClick={() => setCommsModalOpen(true)}
                                            fullWidth
                                            sx={{ justifyContent: 'flex-start', borderColor: '#e5e7eb' }}
                                        >
                                            Log Communication
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}

                        {/* Timeline Tab */}
                        {activeTab === 1 && (
                            <MaternityTimeline record={record} />
                        )}

                        {/* KIT Days Tab */}
                        {activeTab === 2 && (
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Keeping in Touch Days
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Employees can work up to 10 KIT days during maternity leave
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => setKitModalOpen(true)}
                                        disabled={(record.kit_days?.length || 0) >= 10}
                                        sx={{ 
                                            background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                            '&:hover': { opacity: 0.9 }
                                        }}
                                    >
                                        Add KIT Day
                                    </Button>
                                </Box>

                                <Card variant="outlined" sx={{ mb: 3, bgcolor: '#f5f3ff' }}>
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={((record.kit_days?.length || 0) / 10) * 100} 
                                                sx={{ 
                                                    height: 12, 
                                                    borderRadius: 6,
                                                    bgcolor: '#e5e7eb',
                                                    '& .MuiLinearProgress-bar': { bgcolor: '#8b5cf6' }
                                                }} 
                                            />
                                        </Box>
                                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#8b5cf6' }}>
                                            {record.kit_days?.length || 0}/10
                                        </Typography>
                                    </CardContent>
                                </Card>

                                {record.kit_days?.length > 0 ? (
                                    <List>
                                        {record.kit_days.map((kit, index) => (
                                            <ListItem 
                                                key={index}
                                                sx={{ 
                                                    border: '1px solid #e5e7eb', 
                                                    borderRadius: 2, 
                                                    mb: 1,
                                                    bgcolor: 'white'
                                                }}
                                            >
                                                <ListItemIcon>
                                                    <Box sx={{ 
                                                        width: 40, 
                                                        height: 40, 
                                                        borderRadius: 2, 
                                                        bgcolor: '#f5f3ff', 
                                                        color: '#8b5cf6',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        <KITIcon />
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={formatDate(kit.kit_date)}
                                                    secondary={`${kit.hours_worked} hours — ${kit.description || 'No description'}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No KIT days recorded yet
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Communications Tab */}
                        {activeTab === 3 && (
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Communication Log
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => setCommsModalOpen(true)}
                                        sx={{ 
                                            background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                            '&:hover': { opacity: 0.9 }
                                        }}
                                    >
                                        Log Communication
                                    </Button>
                                </Box>

                                {record.communications?.length > 0 ? (
                                    <List>
                                        {record.communications.map((comm, index) => (
                                            <ListItem 
                                                key={index}
                                                sx={{ 
                                                    border: '1px solid #e5e7eb', 
                                                    borderRadius: 2, 
                                                    mb: 1,
                                                    bgcolor: 'white'
                                                }}
                                            >
                                                <ListItemIcon>
                                                    <Box sx={{ 
                                                        width: 40, 
                                                        height: 40, 
                                                        borderRadius: 2, 
                                                        bgcolor: comm.direction === 'inbound' ? '#dbeafe' : '#f5f3ff',
                                                        color: comm.direction === 'inbound' ? '#3b82f6' : '#8b5cf6',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        {comm.method === 'email' ? <EmailIcon /> : <CommsIcon />}
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={comm.summary}
                                                    secondary={
                                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                            <Chip label={comm.type} size="small" variant="outlined" />
                                                            <Chip label={comm.method} size="small" variant="outlined" />
                                                            <Chip 
                                                                label={comm.direction} 
                                                                size="small" 
                                                                color={comm.direction === 'inbound' ? 'info' : 'default'}
                                                            />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formatDate(comm.communication_date)}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Box sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No communications logged yet
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Container>

            {/* KIT Day Modal */}
            <KITDayForm
                open={kitModalOpen}
                onClose={() => setKitModalOpen(false)}
                maternityId={id}
                onSuccess={() => {
                    setKitModalOpen(false);
                    fetchRecord();
                }}
            />

            {/* Letter Generator Modal */}
            <MaternityLetterGenerator
                open={letterModalOpen}
                onClose={() => setLetterModalOpen(false)}
                maternityId={id}
                record={record}
                onSuccess={() => {
                    setLetterModalOpen(false);
                    fetchRecord();
                }}
            />

            {/* Communications Modal */}
            <Dialog open={commsModalOpen} onClose={() => setCommsModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Log Communication
                        <IconButton onClick={() => setCommsModalOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            select
                            label="Type"
                            value={commsForm.type}
                            onChange={(e) => setCommsForm({ ...commsForm, type: e.target.value })}
                            fullWidth
                        >
                            <MenuItem value="general">General</MenuItem>
                            <MenuItem value="kit_day">KIT Day Discussion</MenuItem>
                            <MenuItem value="return_discussion">Return Discussion</MenuItem>
                            <MenuItem value="welfare">Welfare Check</MenuItem>
                        </TextField>
                        <TextField
                            select
                            label="Method"
                            value={commsForm.method}
                            onChange={(e) => setCommsForm({ ...commsForm, method: e.target.value })}
                            fullWidth
                        >
                            <MenuItem value="email">Email</MenuItem>
                            <MenuItem value="phone">Phone</MenuItem>
                            <MenuItem value="letter">Letter</MenuItem>
                            <MenuItem value="in_person">In Person</MenuItem>
                        </TextField>
                        <TextField
                            select
                            label="Direction"
                            value={commsForm.direction}
                            onChange={(e) => setCommsForm({ ...commsForm, direction: e.target.value })}
                            fullWidth
                        >
                            <MenuItem value="outbound">Outbound (to employee)</MenuItem>
                            <MenuItem value="inbound">Inbound (from employee)</MenuItem>
                        </TextField>
                        <TextField
                            label="Summary"
                            value={commsForm.summary}
                            onChange={(e) => setCommsForm({ ...commsForm, summary: e.target.value })}
                            multiline
                            rows={3}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setCommsModalOpen(false)}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleLogCommunication}
                        sx={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MaternityDetail;